/**
 * Progress counts, section 5.
 *
 * "Daily habits (water, steps, vitamin D) are tracked but deliberately excluded from
 * dayProgress, so the ring is achievable."
 */
import type { PlanState } from './types.ts';
import { sessionFor } from './session.ts';
import { effectiveSessionId } from './reschedule.ts';
import type { SessionId } from './session.ts';
import { slotsFor, mealsTicked } from './nutrition.ts';

export const HABITS = ['water', 'steps', 'vitd'] as const;
export type HabitName = (typeof HABITS)[number];

export interface Counts { done: number; total: number }

export function ratio(c: Counts): number {
  return c.total === 0 ? 0 : c.done / c.total;
}

/** Ticked sets over total sets for the day's session. */
export function sessionProgress(
  state: PlanState, startDate: string, week: number, dayIndex: number, id: SessionId,
): Counts {
  const session = sessionFor(state, startDate, week, id);
  const total = session.totalTickTargets;
  let done = 0;
  if ('ex' in session) {
    session.ex.forEach((e, exIndex) => {
      const targets = 'plannedSets' in e ? (e as { plannedSets: number }).plannedSets : e.s;
      for (let i = 0; i < targets; i++) {
        if (state.get(`set:${dayIndex}:${id}:${exIndex}:${i}`) === 1) done++;
      }
    });
  } else {
    session.blocks.forEach((b, blockIndex) => {
      for (let i = 0; i < b.s; i++) {
        if (state.get(`set:${dayIndex}:${id}:${blockIndex}:${i}`) === 1) done++;
      }
    });
  }
  return { done, total };
}

/** (ticked sets + ticked meals) / (total sets + total slots). Habits excluded. */
export function dayProgress(
  state: PlanState, startDate: string, dayIndex: number,
): Counts {
  const week = Math.floor(dayIndex / 7) + 1;
  const id = effectiveSessionId(state, dayIndex);
  // A rest day has no sets to tick, so only the eating slots count toward the ring.
  const sets = id === 'rest'
    ? { done: 0, total: 0 }
    : sessionProgress(state, startDate, week, dayIndex, id);
  const slots = slotsFor(state, dayIndex);
  return {
    done: sets.done + mealsTicked(state, dayIndex),
    total: sets.total + slots.length,
  };
}

export function habitsFor(state: PlanState, dayIndex: number): Record<HabitName, boolean> {
  return {
    water: state.get(`habit:water:${dayIndex}`) === 1,
    steps: state.get(`habit:steps:${dayIndex}`) === 1,
    vitd: state.get(`habit:vitd:${dayIndex}`) === 1,
  };
}

export function weekComplete(state: PlanState, startDate: string, week: number): boolean {
  for (let d = (week - 1) * 7; d < week * 7; d++) {
    const p = dayProgress(state, startDate, d);
    if (p.total === 0 || p.done < p.total) return false;
  }
  return true;
}

/** Working weight for an exercise: keyed per PHASE, not per day, so entering it once
 *  on the Monday of week 5 pre-fills weeks 6, 7 and 8. */
export function loadFor(
  state: PlanState, phase: number, id: SessionId, exIndex: number,
): string | undefined {
  return state.get<string>(`load:${phase}:${id}:${exIndex}`);
}
